import { Injectable } from '@angular/core';
import { AttendeeAnswer, AttendeeAnswers, Chart, IMultiSeriesChart, IAttendeeAnswers, IRegistrationDetailsForChart, RegistrationDetailsForChart } from '@shared/models/charts-model';
import { ICustomField } from '@routes/events/models/eventpage-model';
import { IAttendeeRegistrationDetail } from '@routes/events/models/attendeelist-model';
import { TranslationService } from '../translation/translation.service';
import { CustomFieldType, TypeOfTimeSlot } from '@shared/enums/CustomFieldType';
import { FreeTextFormat } from '@shared/enums/FreeTextFormat';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { SetDateBasedOnTimeString } from '@shared/constants/constants';

@Injectable()
export class ChartService {
    private excludedFieldTypes = [CustomFieldType.TextBlock, CustomFieldType.FileUpload];

    constructor(
        private translationService: TranslationService,
        private translateService: TranslateService
    ) { }

    public buildChartData(questions: ICustomField[], attendeeDetails: IAttendeeRegistrationDetail[], language: string, defaultLanguage: string): IRegistrationDetailsForChart[] {
        const chartData: IRegistrationDetailsForChart[] = [];

        // This is used for the maximum value of the x axis
        let xScaleMax = 0;

        // We can't create charts for field-types that aren't real questions
        questions = questions?.filter(q => !this.excludedFieldTypes.includes(q.customFieldType));

        questions?.forEach(question => {
            const chart = [];
            const attendeeAnswer = [];
            const attendeeAnswers = [];
            let amount = 0;

            switch (question?.customFieldType) {

                // Custom field is freetext
                case CustomFieldType.FreeText:
                    // Set amount to 0
                    let amountFreeText = 0;

                    // Go through each custom field question of each attendee
                    attendeeDetails?.forEach(attendee => {
                        let answer = null;
                        attendee?.customFields?.forEach(attendeeQuestion => {
                            switch (attendeeQuestion?.freeTextFormat) {
                                case FreeTextFormat.Text:
                                    if (attendeeQuestion?.id === question?.id && attendeeQuestion?.textValue && attendeeQuestion?.textValue?.trim() !== '') {
                                        amountFreeText++;
                                        answer = attendeeQuestion?.textValue;
                                    }
                                    break;
                                case FreeTextFormat.CountryCode:
                                    if (attendeeQuestion?.id === question?.id && attendeeQuestion?.textValue && attendeeQuestion?.textValue?.trim() !== '') {
                                        amountFreeText++;
                                        answer = attendeeQuestion?.textValue;
                                    }
                                    break;
                                case FreeTextFormat.Number:
                                    if (attendeeQuestion?.id === question?.id && attendeeQuestion?.numberValue) {
                                        amountFreeText++;
                                        answer = attendeeQuestion?.numberValue?.toString();
                                    }
                                    break;
                                case FreeTextFormat.Date:
                                    if (attendeeQuestion?.id === question?.id && attendeeQuestion?.dateTimeValue) {
                                        amountFreeText++;
                                        answer = moment.utc(attendeeQuestion?.dateTimeValue).local().format('L');
                                    }
                                    break;
                                case FreeTextFormat.Time:
                                    if (attendeeQuestion?.id === question?.id && attendeeQuestion?.textValue && attendeeQuestion?.textValue?.trim() !== '') {
                                        amountFreeText++;
                                        answer = attendeeQuestion?.textValue;
                                    }
                                    break;
                            }
                        });

                        // Push data to attendeeAnswer object when there is an answer, needed for every answer of a question
                        if (answer && answer?.trim() !== '') {
                            attendeeAnswer.push(new AttendeeAnswer(
                                attendee.firstName,
                                attendee.lastNamePrefix,
                                attendee.lastName,
                                attendee.ticketHolderFirstName,
                                attendee.ticketHolderLastName,
                                attendee.isInvitee,
                                attendee.contactFirstName,
                                attendee.contactLastNamePrefix,
                                attendee.contactLastName,
                                attendee.contactMobile,
                                attendee.contactCompany,
                                attendee.email,
                                attendee.mobile,
                                attendee.company,
                                answer,
                                null
                            ));
                        }
                    });

                    // Push data to chart object, needed for plotting the chart
                    chart.push(new Chart(
                        this.translateService.instant('ROUTES.EVENTDETAIL.COMPONENTS.REGISTRATIONDETAILS.ALLANSWERS.LABEL'),
                        amountFreeText
                    ));

                    // Push data to attendeeAnswers object, needed in the detail modal
                    attendeeAnswers.push(new AttendeeAnswers(
                        this.translateService.instant('ROUTES.EVENTDETAIL.COMPONENTS.REGISTRATIONDETAILS.ALLANSWERS.LABEL'),
                        true,
                        attendeeAnswer
                    ));

                    // Set amount to amount of answers, needed to show next to the question name
                    amount = amountFreeText;

                    // Set maximum value of x axis when the current amount is larget than the current value
                    if (amount > xScaleMax) {
                        xScaleMax = amount;
                    }

                    break;


                // Custom field is single select
                case CustomFieldType.SingleSelect:
                    // Check if custom field is timeslot
                    let isTimeslotSingleSelect = false;
                    isTimeslotSingleSelect = question?.multipleChoiceConfigOptions?.typeOfTimeSlot === TypeOfTimeSlot.DateAndTime;

                    // Go through each option
                    question?.customFieldOptions?.forEach(option => {

                        if (option.childrenIds?.length > 0) {
                            return;
                        }

                        let amountSingleSelectOption = 0;

                        let parentAnswer = '';
                        if (option.parentId) {
                            const parentOption = question.customFieldOptions.find(o => o.id === option.parentId);
                            parentAnswer = this.translationService.getTranslationValue(parentOption.translations, language, defaultLanguage) || '';
                            if (parentAnswer) {
                                parentAnswer += ' - ';
                            }
                        }

                        // Go through each custom field question option of each attendee
                        attendeeDetails?.forEach(attendee => {
                            let answer = null;
                            attendee?.customFields?.forEach(attendeeQuestion => {
                                if (attendeeQuestion?.customFieldOptions?.find(x => x.id === option?.id)) {
                                    amountSingleSelectOption++;
                                    answer = this.translationService.getTranslationValue(option?.translations, language, defaultLanguage);
                                }
                            });

                            // Push data to attendeeAnswer object when there is an answer, needed for every answer of a question
                            if (answer && answer?.trim() !== '') {
                                attendeeAnswer.push(new AttendeeAnswer(
                                    attendee.firstName,
                                    attendee.lastNamePrefix,
                                    attendee.lastName,
                                    attendee.ticketHolderFirstName,
                                    attendee.ticketHolderLastName,
                                    attendee.isInvitee,
                                    attendee.contactFirstName,
                                    attendee.contactLastNamePrefix,
                                    attendee.contactLastName,
                                    attendee.contactMobile,
                                    attendee.contactCompany,
                                    attendee.email,
                                    attendee.mobile,
                                    attendee.company,
                                    answer,
                                    option?.id
                                ));
                            }
                        });

                        // Build answer
                        let chartAnswerSingleSelect = parentAnswer + this.translationService.getTranslationValue(option?.translations, language, defaultLanguage);
                        if (isTimeslotSingleSelect) {
                            if (chartAnswerSingleSelect === '-') {
                                chartAnswerSingleSelect = '';
                            }
                            if (chartAnswerSingleSelect && chartAnswerSingleSelect?.trim() !== '') {
                                chartAnswerSingleSelect += ' ';
                            }
                            chartAnswerSingleSelect += moment(option?.timeSlot?.begin)?.format('LT') + ' - ' + moment(option?.timeSlot?.end)?.format('LT');
                        }

                        // Push data to chart object, needed for plotting the chart
                        chart.push(new Chart(
                            chartAnswerSingleSelect,
                            amountSingleSelectOption
                        ));

                        // Push data to attendeeAnswers object, needed in the detail modal
                        attendeeAnswers.push(new AttendeeAnswers(
                            chartAnswerSingleSelect,
                            false,
                            attendeeAnswer.filter(x => x.optionId === option?.id)
                        ));

                        // Add the amount op the options to the total amount of the question
                        amount += amountSingleSelectOption;

                    });

                    // Set maximum value of x axis when the current amount is larget than the current value
                    if (amount > xScaleMax) {
                        xScaleMax = amount;
                    }

                    break;


                // Custom field is multiple select
                case CustomFieldType.MultipleSelect:
                    // Check if custom field is timeslot
                    let isTimeslotMultipleSelect = false;
                    isTimeslotMultipleSelect = question?.multipleChoiceConfigOptions?.typeOfTimeSlot === TypeOfTimeSlot.DateAndTime;

                    // Go through each option
                    question?.customFieldOptions?.forEach(option => {
                        let amountMultiSelectOption = 0;

                        // Go through each custom field question option of each attendee
                        attendeeDetails?.forEach(attendee => {
                            let answer = null;
                            attendee?.customFields?.forEach(attendeeQuestion => {
                                if (attendeeQuestion?.customFieldOptions?.find(x => x.id === option?.id)) {
                                    amountMultiSelectOption++;
                                    answer = this.translationService.getTranslationValue(option?.translations, language, defaultLanguage);

                                    // Add time when custom field is timeslot
                                    if (isTimeslotMultipleSelect) {
                                        if (answer && answer?.trim() !== '') {
                                            answer += ' ';
                                        }
                                        answer += moment(option?.timeSlot?.begin)?.format('LT') + ' - ' + moment(option?.timeSlot?.end)?.format('LT');
                                    }
                                }
                            });

                            // Push data to attendeeAnswer object when there is an answer, needed for every answer of a question
                            if (answer && answer?.trim() !== '') {
                                attendeeAnswer.push(new AttendeeAnswer(
                                    attendee.firstName,
                                    attendee.lastNamePrefix,
                                    attendee.lastName,
                                    attendee.ticketHolderFirstName,
                                    attendee.ticketHolderLastName,
                                    attendee.isInvitee,
                                    attendee.contactFirstName,
                                    attendee.contactLastNamePrefix,
                                    attendee.contactLastName,
                                    attendee.contactMobile,
                                    attendee.contactCompany,
                                    attendee.email,
                                    attendee.mobile,
                                    attendee.company,
                                    answer,
                                    option?.id
                                ));
                            }
                        });

                        // Build answer
                        let chartAnswerMultipleSelect = this.translationService.getTranslationValue(option?.translations, language, defaultLanguage);
                        if (isTimeslotMultipleSelect) {
                            if (chartAnswerMultipleSelect === '-') {
                                chartAnswerMultipleSelect = '';
                            }
                            if (chartAnswerMultipleSelect && chartAnswerMultipleSelect?.trim() !== '') {
                                chartAnswerMultipleSelect += ' ';
                            }
                            chartAnswerMultipleSelect += moment(option?.timeSlot?.begin)?.format('LT') + ' - ' + moment(option?.timeSlot?.end)?.format('LT');
                        }

                        // Push data to chart object, needed for plotting the chart
                        chart.push(new Chart(
                            chartAnswerMultipleSelect,
                            amountMultiSelectOption
                        ));

                        // Push data to attendeeAnswers object, needed in the detail modal
                        attendeeAnswers.push(new AttendeeAnswers(
                            chartAnswerMultipleSelect,
                            false,
                            attendeeAnswer.filter(x => x.optionId === option?.id)
                        ));

                        // Add the amount op the options to the total amount of the question
                        amount += amountMultiSelectOption;

                        // Set maximum value of x axis when the current amount is larget than the current value
                        if (amountMultiSelectOption > xScaleMax) {
                            xScaleMax = amountMultiSelectOption;
                        }
                    });

                    break;


                // Custom field is boolean
                case CustomFieldType.Boolean:
                    // Set amount to 0 for both type of answers
                    let amountOptIn = 0;
                    let amountOptOut = 0;

                    // Go through each custom field question of each attendee
                    attendeeDetails?.forEach(attendee => {
                        let answer = null;
                        attendee?.customFields?.forEach(attendeeQuestion => {
                            switch (attendeeQuestion?.booleanValue) {
                                case true:
                                    if (attendeeQuestion?.id === question?.id) {
                                        amountOptIn++;
                                        answer = this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.OPTIN');
                                    }
                                    break;
                                default:
                                    if (attendeeQuestion?.id === question?.id) {
                                        amountOptOut++;
                                        answer = this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.OPTOUT');
                                    }
                                    break;
                            }
                        });

                        // Push data to attendeeAnswer object, needed for every answer of a question
                        attendeeAnswer.push(new AttendeeAnswer(
                            attendee.firstName,
                            attendee.lastNamePrefix,
                            attendee.lastName,
                            attendee.ticketHolderFirstName,
                            attendee.ticketHolderLastName,
                            attendee.isInvitee,
                            attendee.contactFirstName,
                            attendee.contactLastNamePrefix,
                            attendee.contactLastName,
                            attendee.contactMobile,
                            attendee.contactCompany,
                            attendee.email,
                            attendee.mobile,
                            attendee.company,
                            answer,
                            null
                        ));
                    });

                    // Push data to chart object, needed for plotting the chart
                    // For boolean, we always have true (optin) and false (optout)
                    chart.push(new Chart(
                        this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.OPTIN'),
                        amountOptIn
                    ), new Chart(
                        this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.OPTOUT'),
                        amountOptOut
                    ));

                    // Push data to attendeeAnswers object, needed in the detail modal
                    attendeeAnswers.push(new AttendeeAnswers(
                        this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.OPTIN'),
                        false,
                        attendeeAnswer.filter(x => x.answer === this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.OPTIN'))
                    ), new AttendeeAnswers(
                        this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.OPTOUT'),
                        false,
                        attendeeAnswer.filter(x => x.answer === this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.OPTOUT'))
                    ));

                    // Set amount to amount of answers, needed to show next to the question name
                    amount = amountOptIn + amountOptOut;

                    // Set maximum value of x axis when the current amount is larget than the current value
                    if (amount > xScaleMax) {
                        xScaleMax = amount;
                    }

                    break;

                    // Custom field is Sign document
                case CustomFieldType.SignDocument:
                    // Set amount to 0 for both type of answers
                    let amountSigned = 0;
                    let amountNotSigned = 0;

                    // Go through each custom field question of each attendee
                    attendeeDetails?.forEach(attendee => {
                        let answer = null;
                        attendee?.customFields?.forEach(attendeeQuestion => {
                            if(!attendeeQuestion.customFieldDocuments || attendeeQuestion.customFieldDocuments.length === 0)
                            {
                                if (attendeeQuestion?.id === question?.id) {
                                amountNotSigned++;
                                answer = this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.NOTSIGNED');
                                }
                            } else {
                                if (attendeeQuestion?.id === question?.id) {
                                    amountSigned++;
                                    answer = this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.SIGNED');
                                }
                            }
                        });

                        // Push data to attendeeAnswer object, needed for every answer of a question
                        attendeeAnswer.push(new AttendeeAnswer(
                            attendee.firstName,
                            attendee.lastNamePrefix,
                            attendee.lastName,
                            attendee.ticketHolderFirstName,
                            attendee.ticketHolderLastName,
                            attendee.isInvitee,
                            attendee.contactFirstName,
                            attendee.contactLastNamePrefix,
                            attendee.contactLastName,
                            attendee.contactMobile,
                            attendee.contactCompany,
                            attendee.email,
                            attendee.mobile,
                            attendee.company,
                            answer,
                            null
                        ));
                    });

                    // Push data to chart object, needed for plotting the chart
                    // For boolean, we always have true (optin) and false (optout)
                    chart.push(new Chart(
                        this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.SIGNED'),
                        amountSigned
                    ), new Chart(
                        this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.NOTSIGNED'),
                        amountNotSigned
                    ));

                    // Push data to attendeeAnswers object, needed in the detail modal
                    attendeeAnswers.push(new AttendeeAnswers(
                        this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.SIGNED'),
                        false,
                        attendeeAnswer.filter(x => x.answer === this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.SIGNED'))
                    ), new AttendeeAnswers(
                        this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.NOTSIGNED'),
                        false,
                        attendeeAnswer.filter(x => x.answer === this.translateService.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOM.FORMAT.NOTSIGNED'))
                    ));

                    // Set amount to amount of answers, needed to show next to the question name
                    amount = amountSigned + amountNotSigned;

                    // Set maximum value of x axis when the current amount is larget than the current value
                    if (amount > xScaleMax) {
                        xScaleMax = amount;
                    }

                    break;
            }

            // Push all data to the main object, used for looping through the questions
            chartData.push(new RegistrationDetailsForChart(
                question.id,
                this.translationService.getTranslationValue(question?.translations, language, defaultLanguage),
                amount,
                false,
                chart,
                attendeeAnswers,
                xScaleMax
            ));
        });

        return chartData;
    }

    public getAttendeeAnswers(attendeeAnswers: IAttendeeAnswers[], name: string): IAttendeeAnswers {
        // Filter answer for question
        // This is needed because answers other than freetext can contain multiple answers
        return attendeeAnswers?.filter(x => x.name === name)[0];
    }

    public getInvitationStatusChartData(
        confirmed: number,
        pending: number,
        waiting: number,
        declined: number,
        cancelled?: number
    ): IMultiSeriesChart[] {

        const multiSeries: IMultiSeriesChart[] = [];
        const chart: Chart[] = [];

        chart.push({
            name: this.translateService.instant(
                'LEGEND.LABEL.CONFIRMED'
            ),
            value: confirmed,
        });

        chart.push({
            name: this.translateService.instant(
                'LEGEND.LABEL.PENDING'
            ),
            value: pending,
        });

        chart.push({
            name: this.translateService.instant(
                'LEGEND.LABEL.WAITING'
            ),
            value: waiting,
        });

       chart.push({
            name: this.translateService.instant(
                'LEGEND.LABEL.DECLINEDEXPIREDCANCELLED'
            ),
            value: declined,
        });

        chart.push({
            name: this.translateService.instant(
                'LEGEND.LABEL.CANCELLED'
            ),
            value: cancelled,
        });

        multiSeries.push({ name: '', series: chart });

        return multiSeries;
    }

}
